import React, { useEffect, useRef, useState } from 'react';
import SpellControlsBar from './SpellControlsBar';
import styles from './SpellAreaPreview.module.scss';
import { spellArea, mapAreaSquares, buildSpellAreaPlacement, areaTargetIds } from '../../../shared/rules/effects/spellAreaGeometry.mjs';
import { getFootprintSquares, getMapGridDimensions, tokenAnchorCell } from '../utils/pushGeometryCore.mjs';
import { teleportSquares } from '../../../shared/rules/effects/teleportSquares.mjs';
import { pathCells, pathEdges, pathFeet, ringPoints, wallBand } from '../../../shared/rules/effects/spellWalls.mjs';

/**
 * A teleport (Blink Steps, Cloud's Jaunt) picks a square, not an area: the squares the creature can reach are
 * highlighted (the shared rule the server checks), the hovered one shows the footprint, and a click sends that square.
 */
function TeleportSquaresPreview({ targeting, map, allTokens = [], sourceToken, getCoordinates, onAreaSelected }) {
  const [hover, setHover] = useState(null);
  const { columns, rows } = getMapGridDimensions(map);
  const sizeOf = token => getFootprintSquares(token?.size ?? token?.creatureSize);
  const size = sizeOf(sourceToken);
  const origin = tokenAnchorCell(sourceToken, map);
  const blockers = allTokens.filter(token => token?.characterId !== sourceToken?.characterId)
    .map(token => { const anchor = tokenAnchorCell(token, map); return anchor && { ...anchor, size: sizeOf(token) }; }).filter(Boolean);
  const squares = teleportSquares({ origin, size, maxFeet: targeting.area.rangeFeet, columns, rows, blockers });
  const key = square => `${square.x},${square.y}`;
  const allowed = new Set(squares.map(key));
  const squareAt = (event) => {
    const point = getCoordinates(event.clientX, event.clientY);
    if (!point) return null;
    const square = { x: Math.min(Math.floor(point.x * columns), columns - size), y: Math.min(Math.floor(point.y * rows), rows - size) };
    return allowed.has(key(square)) ? square : null;
  };
  const move = (event) => setHover(squareAt(event));
  return <>
    <svg data-testid="teleport-squares" role="img" aria-label={`${targeting.label}: ${squares.length} squares in reach`}
      viewBox={`0 0 ${columns} ${rows}`} preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 20, cursor: hover ? 'pointer' : 'not-allowed', touchAction: 'none' }}
      onPointerMove={move} onMouseMove={move}
      onClick={(event) => {
        event.preventDefault(); event.stopPropagation();
        const square = squareAt(event);
        if (square) onAreaSelected({ targetIds: [], placement: { mapId: map.mapId, point: { x: (square.x + 0.5) / columns, y: (square.y + 0.5) / rows } } });
      }}>
      <rect width={columns} height={rows} fill="transparent" />
      {squares.map(square => <rect key={key(square)} data-testid="teleport-square" x={square.x + 0.06} y={square.y + 0.06} width={0.88} height={0.88} rx={0.12}
        fill="rgba(120,190,255,0.22)" stroke="rgba(150,205,255,0.7)" strokeWidth="0.04" pointerEvents="none" />)}
      {hover && <rect data-testid="teleport-hover" x={hover.x} y={hover.y} width={size} height={size} rx={0.15}
        fill="rgba(150,205,255,0.45)" stroke="#bfe3ff" strokeWidth="0.08" pointerEvents="none" />}
    </svg>
    {!squares.length && <SpellControlsBar label={targeting.label} error="No square in reach is free. Cancel and try again after moving." />}
  </>;
}

/**
 * A spell wall is drawn, not placed: clicks or a drag walk along the grid lines (or across squares, for a thick wall)
 * with the length against the spell's limit, and the next piece shows faintly under the pointer. Ring and Forcecage's
 * forms take one click for the centre. Undo takes back the last click or drag. The server checks it all.
 */
function WallPreview({ targeting, map, getCoordinates, onAreaSelected }) {
  const { area } = targeting;
  const { columns, rows } = getMapGridDimensions(map);
  const [points, setPoints] = useState([]);
  const [ring, setRing] = useState(false);
  const [side, setSide] = useState(1);
  const [form, setForm] = useState(area.forms?.[0] || null);
  const [hover, setHover] = useState(null);
  // Where each click or drag began in `points`, so Undo takes back a whole stroke.
  const [strokes, setStrokes] = useState([]);
  const painting = useRef(false);
  const pressed = useRef(false);
  const ringFeet = form?.feet || (ring ? area.ringFeet : 0);
  const shown = ringFeet && points[0] ? ringPoints(points[0], ringFeet) : points;
  const thick = Boolean(area.thick);
  const feet = pathFeet(shown, { thick });
  const tooLong = !ringFeet && feet > area.limitFeet;
  const ready = ringFeet ? points.length === 1 : feet > 0 && !tooLong;
  const cellAt = (event) => {
    const point = getCoordinates(event.clientX, event.clientY);
    if (!point) return null;
    return thick && !ringFeet ? { x: Math.min(columns - 1, Math.floor(point.x * columns)), y: Math.min(rows - 1, Math.floor(point.y * rows)) }
      : { x: Math.round(point.x * columns), y: Math.round(point.y * rows) };
  };
  const add = (cell) => setPoints(old => (ringFeet ? [cell] : old.at(-1)?.x === cell.x && old.at(-1)?.y === cell.y ? old : [...old, cell]));
  const begin = (cell) => { if (!ringFeet) setStrokes(old => [...old, points.length]); add(cell); };
  const down = (event) => {
    if (event.button > 0) return;
    event.preventDefault(); event.stopPropagation();
    const cell = cellAt(event);
    if (!cell) return;
    pressed.current = true;
    painting.current = !ringFeet;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    begin(cell);
  };
  const move = (event) => {
    const cell = cellAt(event);
    setHover(cell);
    if (cell && painting.current) add(cell);
  };
  const up = () => { painting.current = false; };
  // A press already placed its point; a click with no press first (a keyboard or an older browser) places one now.
  const click = (event) => {
    event.preventDefault(); event.stopPropagation();
    if (pressed.current) { pressed.current = false; return; }
    const cell = cellAt(event);
    if (cell) begin(cell);
  };
  const undo = () => {
    const start = strokes.at(-1);
    if (start != null && start < points.length) { setPoints(points.slice(0, start)); setStrokes(strokes.slice(0, -1)); }
    else setPoints(points.slice(0, -1));
  };
  const clear = () => { setPoints([]); setStrokes([]); };
  // The faint next piece: the ring at the pointer before it is placed, or the leg from the last point to the pointer.
  const ghost = !hover || painting.current ? [] : ringFeet ? (points[0] ? [] : ringPoints(hover, ringFeet)) : points.length ? [points.at(-1), hover] : [hover, hover];
  const finish = () => onAreaSelected({ targetIds: [], placement: { mapId: map.mapId, point: { x: Math.min(1, points[0].x / columns), y: Math.min(1, points[0].y / rows) }, wall: { points, ring: Boolean(ring), side, form: form?.id } } });
  const band = area.hotSide ? wallBand(shown, 2, side) : [];
  return <>
    <svg data-testid="spell-wall-preview" role="img" aria-label={`${targeting.label}: ${feet} feet drawn`} viewBox={`0 0 ${columns} ${rows}`} preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 20, cursor: 'crosshair', touchAction: 'none' }}
      onPointerDown={down} onPointerMove={move} onMouseMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={() => { up(); setHover(null); }} onClick={click}>
      <rect width={columns} height={rows} fill="transparent" />
      {thick && !ringFeet && pathCells(ghost).map(({ x, y }) => <rect key={`ghost-${x},${y}`} data-testid="spell-wall-ghost" x={x} y={y} width="1" height="1" fill="rgba(150,130,255,.2)" stroke="rgba(190,175,255,.7)" strokeWidth="0.05" strokeDasharray="0.15 0.1" pointerEvents="none" />)}
      {(!thick || ringFeet) && pathEdges(ghost).map(edge => { const [x, y, direction] = edge.split(','); const x1 = Number(x), y1 = Number(y);
        return <line key={`ghost-${edge}`} data-testid="spell-wall-ghost" x1={x1} y1={y1} x2={direction === 'h' ? x1 + 1 : x1} y2={direction === 'h' ? y1 : y1 + 1} stroke="rgba(255,179,71,.55)" strokeWidth="0.1" strokeDasharray="0.2 0.12" pointerEvents="none" />; })}
      {hover && (!thick || ringFeet) && <circle data-testid="spell-wall-cursor" cx={hover.x} cy={hover.y} r="0.14" fill="#ffb347" fillOpacity="0.8" pointerEvents="none" />}
      {band.map(({ x, y }) => <rect key={`band-${x},${y}`} x={x} y={y} width="1" height="1" fill="rgba(255,120,60,0.22)" pointerEvents="none" />)}
      {thick && !ringFeet && pathCells(shown).map(({ x, y }) => <rect key={`${x},${y}`} data-testid="spell-wall-cell" x={x} y={y} width="1" height="1" fill={tooLong ? 'rgba(220,60,60,.5)' : 'rgba(150,130,255,.5)'} pointerEvents="none" />)}
      {(!thick || ringFeet) && pathEdges(shown).map(edge => { const [x, y, direction] = edge.split(','); const x1 = Number(x), y1 = Number(y);
        return <line key={edge} data-testid="spell-wall-edge" x1={x1} y1={y1} x2={direction === 'h' ? x1 + 1 : x1} y2={direction === 'h' ? y1 : y1 + 1} stroke={tooLong ? '#e24b4a' : '#ffb347'} strokeWidth="0.16" strokeLinecap="round" pointerEvents="none" />; })}
    </svg>
    <SpellControlsBar label={targeting.label} hint={area.forms ? 'Click the centre of the prison' : ringFeet ? 'Click the centre of the ring' : thick ? 'Click or drag across squares to draw the wall' : 'Click or drag along the grid lines to draw the wall'}
      count={ringFeet ? null : feet} maximum={ringFeet ? null : `${area.limitFeet} ft`}
      countLabel={ringFeet ? `${ringFeet}-foot ${form ? form.label.split(' ').at(-1) : 'ring'}` : `${feet} of ${area.limitFeet} feet`} error={tooLong ? `${targeting.label} reaches only ${area.limitFeet} feet.` : ''}
      actions={<>
        {(area.forms || []).map(item => <button key={item.id} className={styles.undo} type="button" aria-pressed={form?.id === item.id} onClick={() => { setForm(item); clear(); }}>{item.label}</button>)}
        {area.ringFeet > 0 && <button className={styles.undo} type="button" aria-pressed={ring} onClick={() => { setRing(old => !old); clear(); }}>{ring ? 'Line' : 'Ring'}</button>}
        {(area.hotSide || area.chooseSide) && <button className={styles.undo} type="button" onClick={() => setSide(old => -old)}>{area.hotSide ? 'Flip hot side' : 'Flip push side'}</button>}
        <button className={styles.undo} type="button" disabled={!points.length} onClick={undo}>Undo</button>
        <button className={styles.cast} type="button" disabled={!ready} onClick={finish}><span aria-hidden="true">✦</span> Place wall</button>
      </>} />
  </>;
}

export default function SpellAreaPreview(props) {
  if (props.targeting?.area?.shape === 'wall') return <WallPreview {...props} />;
  return props.targeting?.area?.shape === 'teleport' ? <TeleportSquaresPreview {...props} /> : <AreaPreview {...props} />;
}

function AreaPreview({ targeting, map, tokens, sourceToken, getCoordinates, onPreviewChange, onAreaSelected, barriers }) {
  const [points, setPoints] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState('');
  const { columns, rows } = getMapGridDimensions(map);
  const multi = targeting.area.maxAreas > 1;
  const maximum = targeting.area.maxAreas || 1;
  const sourceSize = sourceToken?.size;
  useEffect(() => { setPoints([]); setCursor(null); setError(''); onPreviewChange(null); }, [targeting, onPreviewChange]);

  const shapesFor = (selected, hover) => [...selected, ...(hover && selected.length < maximum ? [hover] : [])]
    .map((point) => spellArea({ area: targeting.area, point, sourceToken, sourceSize, map })).filter(Boolean);
  const shapes = shapesFor(points, cursor);
  // The squares the server counts (shared rule): those at least half covered by a template, and not walled off from its origin.
  const squares = shapes.length ? mapAreaSquares({ geometry: { shape: 'composite', shapes }, map, barriers }) : [];
  const preview = (selected, hover) => onPreviewChange({ shape: 'composite', shapes: shapesFor(selected, hover) });
  const submit = (selected) => {
    const placement = { mapId: map.mapId, ...(multi ? { points: selected } : { point: selected[0] }) };
    try {
      const geometry = buildSpellAreaPlacement({ area: targeting.area, placement, sourceToken, sourceSize, map });
      const targetIds = areaTargetIds({ geometry, tokens, map, barriers, sourceCombatantId: targeting.sourceCombatantId, allowZeroHp: targeting.actionType === 'healing' });
      if (!targetIds.length && !targeting.area.allowEmpty) throw new Error('The areas contain no living combat participants.');
      onAreaSelected({ targetIds, placement });
    } catch (failure) { setError(failure.message); }
  };
  const move = (event) => {
    const point = getCoordinates(event.clientX, event.clientY);
    setCursor(point); preview(points, point);
  };

  return <>
    <svg data-testid="spell-area-preview" role="img" aria-label={`${targeting.label} area preview`}
      viewBox={`0 0 ${columns} ${rows}`} preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 20, cursor: 'crosshair', touchAction: 'none' }}
      onPointerMove={move} onMouseMove={move}
      onClick={(event) => {
        event.preventDefault(); event.stopPropagation();
        const point = getCoordinates(event.clientX, event.clientY);
        if (!point) return;
        if (!multi) { submit([point]); return; }
        if (points.length >= maximum) return;
        const nextShape = spellArea({ area: targeting.area, point, sourceToken, sourceSize, map });
        if (!nextShape || points.some((entry) => JSON.stringify(spellArea({ area: targeting.area, point: entry, sourceToken, sourceSize, map })) === JSON.stringify(nextShape))) return;
        const selected = [...points, point]; setPoints(selected); setError(''); preview(selected, null);
      }}>
      <rect width={columns} height={rows} fill="transparent" />
      <g data-testid="spell-area-squares" pointerEvents="none">
        {squares.map(({ x, y }) => <rect key={`${x},${y}`} x={x} y={y} width="1" height="1" fill="rgba(255,145,0,0.3)" stroke="rgba(255,179,71,0.55)" strokeWidth="0.03" />)}
      </g>
      {shapes.map((shape, index) => shape.shape === 'circle'
        ? <circle key={index} cx={shape.center.x} cy={shape.center.y} r={shape.radius} fill="none" stroke="#ffb347" strokeWidth="0.06" strokeDasharray=".2 .12" pointerEvents="none" />
        : <polygon key={index} points={shape.points.map((p) => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#ffb347" strokeWidth="0.06" strokeDasharray=".2 .12" pointerEvents="none" />)}
      {shapes.filter((shape) => shape.origin).map((shape, index) => <circle key={`origin-${index}`} cx={shape.origin.x} cy={shape.origin.y} r="0.12" fill="#ffe0a8" pointerEvents="none" />)}
    </svg>
    {(multi || error) && <SpellControlsBar label={multi ? targeting.label : null}
      hint={points.length < (targeting.area.minAreas || 1) ? 'Click the map to place your areas' : points.length < maximum ? 'Add more areas or confirm your cast' : 'All areas placed · confirm your cast'}
      count={points.length} maximum={multi ? maximum : null} countLabel={`${points.length} of ${maximum} areas placed`} error={error}
      actions={multi && <>
        <button className={styles.undo} type="button" onClick={() => { const selected = points.slice(0, -1); setPoints(selected); setError(''); preview(selected, cursor); }} disabled={!points.length}><span aria-hidden="true">↶</span> Undo area</button>
        <button className={styles.cast} type="button" onClick={() => submit(points)} disabled={points.length < (targeting.area.minAreas || 1)}><span aria-hidden="true">✦</span> Cast areas</button>
      </>} />}

  </>;
}
